/**
 * Dark Ship Layer — renders vessels that went silent in shipping lanes.
 *
 * Listens for analysis:ais-anomaly events with type 'dark-ship' or 'dark-zone'
 * and renders them as warning markers on the map. Shows the vessel's last known
 * position with an expanding ring indicating signal loss.
 */

import { ScatterplotLayer, TextLayer } from '@deck.gl/layers';
import type { Layer } from '@deck.gl/core';
import type { AISAnomaly } from '@/intelligence/analysis/ais-spoofing';
import { initDarkShipCache, getDarkShips } from '@/map/dark-ship-cache';

/** Layer IDs handled by this renderer. */
export const DARK_SHIP_LAYER_IDS = new Set(['dark-ships']);

// ---------------------------------------------------------------------------
// State lives in the shared app-lifetime dark-ship cache — anomalies are
// emitted once per dark period, so a renderer-scoped cache would lose
// them on every flat/globe mode switch.
// ---------------------------------------------------------------------------

/** Ensure the shared anomaly cache is subscribed. Idempotent. */
export function initDarkShipLayer(): void {
  initDarkShipCache();
}

/**
 * Renderer teardown hook. Intentionally does NOT destroy the shared
 * cache — mode switches must not wipe detected dark ships. The cache
 * itself is destroyed at app teardown (destroyDarkShipCache in main).
 */
export function destroyDarkShipLayer(): void {
  // no-op by design
}

// ---------------------------------------------------------------------------
// Layer creation
// ---------------------------------------------------------------------------

/** Create deck.gl layers for dark ship markers. */
export function createDarkShipLayers(zoom: number): Layer[] {
  const anomalies = getDarkShips();
  if (anomalies.length === 0) return [];

  const layers: Layer[] = [];
  const opacity = Math.min(1, 0.4 + zoom * 0.08);

  // Expanding outer ring — signal loss indicator
  const time = (Date.now() % 4000) / 4000; // 4-second cycle
  const expandFactor = 1 + 0.5 * time; // Expands outward over time

  layers.push(new ScatterplotLayer<AISAnomaly>({
    id: 'dark-ship-ring',
    data: anomalies,
    pickable: false,
    filled: false,
    stroked: true,
    lineWidthUnits: 'pixels' as const,
    lineWidthMinPixels: 1,
    radiusUnits: 'pixels' as const,
    getPosition: (d) => [d.lng, d.lat],
    getLineColor: [239, 68, 68, Math.round(150 * (1 - time))], // Fades as it expands
    getLineWidth: 1.5,
    getRadius: () => 12 * expandFactor,
    opacity: opacity * 0.7,
    updateTriggers: {
      getRadius: [time],
      getLineColor: [time],
    },
  }));

  // Core marker — solid red dot at last known position
  layers.push(new ScatterplotLayer<AISAnomaly>({
    id: 'dark-ship-core',
    data: anomalies,
    pickable: true,
    filled: true,
    stroked: true,
    lineWidthUnits: 'pixels' as const,
    lineWidthMinPixels: 1,
    radiusUnits: 'pixels' as const,
    getPosition: (d) => [d.lng, d.lat],
    getFillColor: (d) => {
      // Dark-zone anomalies (sensitive areas) are red, shipping lane dark ships are orange
      return d.anomalyType === 'dark-zone'
        ? [239, 68, 68, 200] as [number, number, number, number]
        : [249, 115, 22, 200] as [number, number, number, number];
    },
    getLineColor: [255, 255, 255, 120],
    getLineWidth: 1,
    getRadius: 6,
    opacity,
  }));

  // Labels at higher zoom levels
  if (zoom >= 5) {
    layers.push(new TextLayer<AISAnomaly>({
      id: 'dark-ship-labels',
      data: anomalies,
      getPosition: (d) => [d.lng, d.lat],
      getText: (d) => {
        const mins = d.silenceDurationSec ? Math.round(d.silenceDurationSec / 60) : '?';
        return `${d.vesselName} (${mins}m dark)`;
      },
      getSize: 10,
      getColor: [255, 200, 200, 200],
      getTextAnchor: 'start' as const,
      getAlignmentBaseline: 'center' as const,
      getPixelOffset: [10, 0],
      fontFamily: 'monospace',
      sizeUnits: 'pixels' as const,
      billboard: true,
      outlineWidth: 2,
      outlineColor: [0, 0, 0, 180],
    }));
  }

  return layers;
}
